/**
 * lib/brand.palette.js
 * ------------------------------------------------------------------
 * THE SCHOOL'S COLOURS. ONE COPY. Everything else reads from here.
 *
 * Before this file the crest palette lived in three places — `school.config.ts`
 * (for PDFs and emails), `tailwind.config.js` (for class names), and again as
 * raw hex literals inside `features/finance/ui.tsx`. The Tailwind copy carried a
 * comment reading "MUST stay in sync with school.config", which is what a
 * codebase says when it has no way to enforce it.
 *
 * The finance kit was the case that proved the point. It was written for this
 * school, then ported to another, and it took its hardcoded navy and gold with
 * it — so a second school's accounting screens spent months wearing Livingstone
 * Kolobeng's colours. Nobody noticed, because hardcoded hex looks exactly as
 * correct as hex that was reasoned about.
 *
 * WHY PLAIN JAVASCRIPT, NOT TYPESCRIPT. `tailwind.config.js` is loaded by Node
 * at build time, outside Vite and outside the TypeScript compiler, so it cannot
 * import a `.ts` file. A `.js` module both sides can read is the only shape that
 * removes the duplication rather than moving it. `brand.palette.d.ts` beside
 * this file gives the TypeScript side its types — deliberately typed as `string`
 * rather than literal hex, because repeating the values in the declaration file
 * would recreate the exact problem this file exists to solve.
 *
 * ADDING A COLOUR. Add it here, and it is available as a Tailwind class
 * (`bg-<name>`, `text-<name>`, `border-l-<name>`) and on `SCHOOL.colors` in the
 * same commit. Add its name to `brand.palette.d.ts` so TypeScript can see it.
 * ------------------------------------------------------------------ */

/**
 * Livingstone Kolobeng College — verified crest palette.
 * Royal/navy blue and gold, taken from the school crest.
 */
export const BRAND = {
  /** The crest navy. Primary buttons, page-header tiles, headings. */
  primary: '#1b3a6b',
  /** Darker navy — the far end of a header gradient. */
  primaryDark: '#142c52',
  /** Lighter navy — hover states, the near end of a tile gradient. */
  primaryLight: '#2d5295',
  /**
   * Navy lifted for a dark background. The crest navy on a dark-grey panel is
   * nearly invisible; this is the same colour at a legible lightness.
   * 6.6:1 on gray-800.
   */
  primarySoft: '#8fb0e0',

  /** The crest gold. Accent bars, the gold header tile. */
  secondary: '#d4a92c',
  /** Gold lifted — the bright end of the gold gradient. */
  secondaryLight: '#e0b845',
  /**
   * Gold deepened for text on white. The crest gold at full strength is
   * unreadable on a light background.
   * NOTE: 2.98:1 on white — under the 3.0 WCAG AA large-text floor, and well
   * under the 4.5 normal-text one. Carried forward unchanged from the finance
   * kit so this refactor stays a refactor. Flagged, not silently altered:
   * darkening it enough to pass (#8a6e1d, 4.85:1) visibly changes the school's
   * gold, which is the school's call and not a side effect of moving a file.
   */
  secondaryDeep: '#b8901f',

  /** Bright gold highlight. */
  accent: '#f0c850',
  /** Near-black navy for body text on light. */
  ink: '#0f1b30',
  /** Neutral grey for secondary text in generated PDFs (gray-500). */
  muted: '#6b7280',

  /**
   * Deep maroon — the study portal's warning colour (something overdue,
   * something a student is failing, the countdown to an exam), which must not
   * be mistaken for the gold accent. Came with the teaching + student portal
   * port from Al-Nur (2026-10-29), at Al-Nur's value. The LKC crest is navy
   * and gold only, so this is a third colour: the school may want its own.
   */
  maroon: '#7b2d3b',
  /** Maroon lifted for a dark background, where #7b2d3b goes almost black. */
  maroonSoft: '#d98a97',
};

/**
 * Hex to the `[r, g, b]` triple jsPDF wants — it will not take a hex string in
 * every position. Derived rather than kept as a second hand-written list, so a
 * colour and its triple cannot disagree.
 */
export function brandRgb(hex) {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}
